import React, { ReactElement, useEffect, useState } from "react";
import { Card, Form } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import { BsBookmarkCheck, BsInfo, BsInfoCircleFill } from "react-icons/bs";
import { IoEllipsisVerticalOutline } from "react-icons/io5";
import { RiArticleLine } from "react-icons/ri";
import TimeAgo from "react-timeago";
import { Loading } from "../../../components";
import ArticleTags from "../../../components/article-tags/ArticleTags";
import Avatar from "../../../components/avatar/Avatar";
import Confirm from "../../../components/confirm/Confirm";
import EditableControl, { ControlType } from "../../../components/editable-text/EditableText";
import Editor from "../../../components/editor/Editor";
import IconUploader from "../../../components/pickers/icon-uploader/IconUploader";
import usePortal from "../../../contexts/PortalContext";
import useRouting, { useParams } from "../../../contexts/Routing";
import {
    GetArticleDocument,
    GetArticlesByTagDocument,
    Permission,
    SearchArticlesDocument,
    useDeleteArticleMutation,
    useGetArticleQuery,
    useGetArticleTagsQuery,
    usePublishArticleMutation,
    useSaveArticleMutation,
    useSetIndexArticleMutation,
    useUnpublishArticleMutation,
} from "../../../__generated__/api";
import { ErrorSecurity, NoDataOrError } from "../../error/Error";
import "./AnswerDetails.scss";

function PublishItem({ id }: {id: string}): ReactElement {
    const [shouldShowPublish, setShouldShowPublish] = useState<boolean>(false);
    const [shouldShowPublishLater, setShouldShowPublishLater] = useState<boolean>(false);
    const [publishDate, setPublishDate] = useState<string>();

    const [doPublish] = usePublishArticleMutation();

    const publish = () => {
        doPublish({
            refetchQueries: [
                { query: GetArticleDocument },
                { query: SearchArticlesDocument },
            ],
            variables: {
                id,
                settings: (!shouldShowPublishLater || !publishDate) ? undefined : {
                    publishDate: new Date(publishDate),
                },
            },
        });
        setShouldShowPublish(false);
    };
    return (
        <>
            <Dropdown.Item data-test="publish-button" eventKey="2" onClick={() => setShouldShowPublish(true)}>
                Publish
            </Dropdown.Item>

            <Confirm
                confirmText={shouldShowPublishLater ? "Schedule Publish" : "Publish"}
                onConfirm={publish}
                shouldShow={shouldShowPublish}
                onHide={() => setShouldShowPublish(false)}
            >
                <div>
                    <h5>Do you want to publish this article?</h5>

                    <Card className="mt-4">
                        <Card.Header className="p-0">
                            <button
                                type="button"
                                className="pe-auto text-start btn btn-none w-100 h-100 m-0"
                                onClick={() => setShouldShowPublishLater(value => !value)}
                            >
                                Publish later
                            </button>

                        </Card.Header>
                        {shouldShowPublishLater && (
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bolder">Schedule Publish</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        placeholder={new Date().toISOString()}
                                        required
                                        value={publishDate}
                                        onChange={value => setPublishDate(value.target.value)}
                                    />
                                    <Form.Text>
                                        Set the time and date you want to publish this article (In your current date.)
                                    </Form.Text>
                                </Form.Group>

                            </Card.Body>
                        )}

                    </Card>

                </div>

            </Confirm>
        </>
    );
}

function UnpublishItem({ id }: {id: string}): ReactElement {
    const [shouldShowUnpublish, setShouldShowUnpublish] = useState<boolean>(false);

    const [doUnpublish] = useUnpublishArticleMutation();

    const publish = () => {
        doUnpublish({
            refetchQueries: [
                { query: GetArticleDocument },
            ],
            variables: {
                id,
            },
        });
        setShouldShowUnpublish(false);
    };
    return (
        <>
            <Dropdown.Item eventKey="2" onClick={() => setShouldShowUnpublish(true)}>
                Unpublish
            </Dropdown.Item>

            <Confirm
                confirmText="Unpublish"
                onConfirm={publish}
                shouldShow={shouldShowUnpublish}
                onHide={() => setShouldShowUnpublish(false)}
            >
                <div>
                    <h5>Do you want to unpublish this article?</h5>

                </div>

            </Confirm>
        </>
    );
}

export default function AnswerDetails(): ReactElement {
    // { handleDelete, handleUpdate }: Props
    const { Paths, historyPush, getSearchParams } = useRouting();
    const { id } = useParams(Paths.ANSWERS_GET);
    const { tagId: defaultTagId } = getSearchParams(Paths.ANSWERS_NEW);

    const [shouldShowDelete, setShouldShowDelete] = useState<boolean>(false);

    const [shouldShowConfirmDefault, setShouldShowConfirmDefault] = useState<boolean>(false);

    const { data: tagsData } = useGetArticleTagsQuery();

    const { permissions } = usePortal();
    const isReadOnly = !permissions.has(Permission.ArticleModify);

    const { loading, error, data } = useGetArticleQuery({
        skip: id === "new",
        variables: {
            id,
        },
    });
    const [deleteAnswer] = useDeleteArticleMutation();
    const [saveAnswer] = useSaveArticleMutation();
    const [setIndexArticle] = useSetIndexArticleMutation();

    const [title, setTitle] = useState("");
    const [iconUrl, setIconUrl] = useState<string | undefined | null>();
    const [html, setHtml] = useState("");
    const [tagIds, setTagIds] = useState<string[]>(id === "new" ? [defaultTagId] : []);
    const [previousId, setPreviousId] = useState<string | undefined>(id === "new" ? undefined : id);

    const [modifiedDate, setModifiedDate] = useState<Date>(() => new Date());

    const [lastTimer, setTimer] = useState<ReturnType<typeof setTimeout>>();

    const save = async () => {
        if ((!html && !title) || (
            data?.article?.title === title
                && data?.article?.body.html === html
                && data.article.iconUrl === iconUrl
                && data.article.tags?.toIdList("id").equals(tagIds))
        ) {
            return;
        }

        const articleId = previousId ?? (id === "new" ? undefined : id);
        const answer = await saveAnswer({
            refetchQueries: tagIds.map(tagId => (
                {
                    query: GetArticlesByTagDocument,
                    variables: {
                        tagId,
                    } as Record<string, string>,
                }
            )),

            variables: {
                id: articleId,
                article: {
                    title,
                    body: html || "<p></p>",
                    iconUrl,
                    tagIds,
                },
            },
        });
        setModifiedDate(new Date(answer.data?.saveArticle.modifiedDate));
        if (articleId !== answer.data?.saveArticle.id) {
            historyPush(Paths.ANSWERS_GET, {
                id: answer.data?.saveArticle.id,
            });
        }
    };
    useEffect(() => {
        if (lastTimer) {
            clearTimeout(lastTimer);
        }
        const timer = setTimeout(save, 500);
        setTimer(timer);
    }, [html, title, iconUrl, tagIds]);

    useEffect(() => {
        if (id !== "new" && !loading) {
            save();
        } else {
            setPreviousId(undefined);
        }
    }, [id, loading]);

    useEffect(() => {
        const beforeUnload = (evt: BeforeUnloadEvent): boolean => {
            const isDirty = !isReadOnly && data?.article?.body.html !== html;
            if (isDirty) {
                evt.preventDefault();
                // eslint-disable-next-line no-param-reassign
                evt.returnValue = "";
            }
            return !isDirty;
        };
        window.addEventListener("beforeunload", beforeUnload);
        return () => {
            window.removeEventListener("beforeunload", beforeUnload);
        };
    }, [html, data]);

    useEffect(() => {
        setPreviousId(data?.article?.id);
        setTitle(data?.article?.title ?? "");
        setHtml(data?.article?.body.html ?? "");
        setModifiedDate(new Date(data?.article?.modifiedDate));
        setIconUrl(data?.article?.iconUrl);
        if (!data && id === "new" && defaultTagId) {
            setTagIds([defaultTagId]);
        } else {
            setTagIds(data?.article?.tags.toIdList("id") ?? []);
        }
    }, [data?.article?.id]);

    useEffect(() => {
        if (id === "new" && defaultTagId) {
            setTagIds([defaultTagId]);
        }
    }, [defaultTagId]);

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return <NoDataOrError error={error} />;
    }

    if (!permissions.has(Permission.ArticleReadPublic)) {
        return <ErrorSecurity title="No access" message="You don't have access to read this answer." />;
    }

    const handleDelete = async () => {
        await deleteAnswer({
            refetchQueries: tagIds.map(tagId => (
                {
                    query: GetArticlesByTagDocument,
                    variables: {
                        tagId,
                    } as Record<string, string>,
                }
            )),
            variables: { id },
        });
        historyPush(Paths.ANSWERS_LIST);
    };

    const handleSetDefault = async () => {
        await setIndexArticle({
            variables: { id },
        });
        setShouldShowConfirmDefault(false);
    };

    const article = data?.article;

    const publishedDate = article?.publishedDate ? new Date(article?.publishedDate) : undefined;
    const isPublished = publishedDate && publishedDate.getTime() < Date.now();

    const tagMap = tagsData?.articleTags.toIdMap("id");
    const selectedTags = tagIds.mapFilter(tag => tagMap?.[tag]);
    return (
        <div className="answer-details mt-0 p-0 h-100">
            <Confirm
                confirmText="Delete"
                onConfirm={handleDelete}
                shouldShow={shouldShowDelete}
                onHide={() => setShouldShowDelete(false)}
            >
                <p>Are you sure you want to delete this answer?</p>
            </Confirm>
            <Confirm
                confirmText="Set Default"
                onConfirm={handleSetDefault}
                closeText="Cancel"
                shouldShow={shouldShowConfirmDefault}
                onHide={() => setShouldShowConfirmDefault(false)}
            >
                <p>
                    By making this page the default, this will be the first page that customers will see when
                    they first open your portal by going to
                    {" "}
                    <a href={`https://${window.location.hostname}/`} target="_blank" rel="noreferrer">
                        https://
                        {window.location.hostname}
                        /
                    </a>
                    .
                </p>
            </Confirm>
            <div className="d-flex align-items-stretch h-100">

                <ArticleTags selectedTags={selectedTags} className="col-2" isReadOnly={isReadOnly} />
                <div className="col">
                    <article className="answer-details-wrap  border shadow">
                        <section className="d-flex align-items-center px-3 p-1 pt-2 border-bottom justify-content-between">
                            <div className="flex-shrink-0 me-3 align-self-start">
                                <IconUploader
                                    value={iconUrl as string}
                                    onChange={icon => setIconUrl(icon)}
                                    shouldStore
                                    isReadOnly={isReadOnly}
                                    shouldShowBorder={false}
                                    defaultIcon={<RiArticleLine className="mx-3 icon-image text-light-300" />}
                                    size="sm"
                                />
                            </div>
                            <div className="flex-grow-1">

                                <h2 className="answer-title">
                                    <EditableControl
                                        isReadOnly={isReadOnly}
                                        value={article?.title}
                                        onChange={value => setTitle(value as string)}
                                    />
                                </h2>
                                {article && (
                                    <h6>
                                        <small className="text-light-400">
                                            Last modified
                                            {" "}
                                            <TimeAgo
                                                date={modifiedDate}
                                                minPeriod={60}
                                                title={
                                                    `${modifiedDate?.toLocaleDateString()} ${modifiedDate?.toLocaleTimeString()}`
                                                }
                                            />
                                            {" "}
                                            by
                                            {" "}
                                            {article.creator?.name ?? "Automation"}
                                            .
                                        </small>
                                    </h6>
                                )}
                                <div className="d-flex">
                                    <EditableControl
                                        type={ControlType.MULTI_SELECT}
                                        isReadOnly={isReadOnly}
                                        value={tagIds}
                                        placeholderClassName="text-light fst-italic"
                                        onChange={tags => {
                                            setTagIds(tags as string[]);
                                        }}
                                        options={tagsData?.articleTags.map(row => ({
                                            value: row.id,
                                            label: row.title,
                                        }))}
                                        placeholder="Click to add tags"
                                    >
                                        <div>
                                            {selectedTags && selectedTags.isNotEmpty
                                                    && selectedTags.map(row => (
                                                        <small key={row.id}>
                                                            <code className="me-2 bg-gray-100 p-1">
                                                                {!row.iconUrl
                                                                    ? "#"
                                                                    : <Avatar src={row.iconUrl} variant="xxs" /> }
                                                                {row.title}
                                                            </code>
                                                        </small>
                                                    ))}
                                            {!isReadOnly && (
                                                <small className="text-light fst-italic border rounded px-2 py-1 add-tags-button">
                                                    Add tags
                                                </small>
                                            )}

                                        </div>

                                    </EditableControl>

                                </div>

                            </div>

                            {!isReadOnly && (
                                <div className="justify-content-end d-flex align-items-center">
                                    {isPublished ? (
                                        <BsBookmarkCheck
                                            className="text-success me-3"
                                            title={
                                                `Published ${publishedDate?.toLocaleString()} by ${article?.publishedBy?.name}`
                                            }
                                        />

                                    ) : (
                                        <em className="text-light me-3">
                                            {publishedDate ? `Publishing ${publishedDate.toLocaleString()}` : "Not Published"}
                                        </em>
                                    )}
                                    <Dropdown>
                                        <Dropdown.Toggle variant="" className="menu-small">
                                            <IoEllipsisVerticalOutline data-test="dropdown" />
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>

                                            <Dropdown.Item data-test="delete-button" onClick={() => setShouldShowDelete(true)}>
                                                Delete
                                            </Dropdown.Item>

                                            <Dropdown.Divider />

                                            {permissions.has(Permission.Administrator) && (
                                                <div title={isPublished
                                                    ? "Set this article as the default. This will make this"
                                                 + "the first page that appears when a customer opens your portal"
                                                    : "To make this article the default, first publish it"}
                                                >
                                                    <Dropdown.Item
                                                        disabled={!isPublished}
                                                        onClick={() => setShouldShowConfirmDefault(true)}

                                                    >
                                                        Set Default
                                                    </Dropdown.Item>
                                                </div>
                                            )}

                                            {isPublished ? (
                                                <UnpublishItem id={article?.id as string} />
                                            ) : (
                                                <PublishItem id={article?.id as string} />
                                            )}

                                        </Dropdown.Menu>
                                    </Dropdown>

                                </div>
                            )}

                        </section>
                        <Editor
                            isReadOnly={isReadOnly}
                            id={article?.id}
                            text={html}
                            onChange={setHtml}
                            shouldAllowAttachments
                            className="p-4"
                            key={article?.id}
                        />
                    </article>
                </div>
            </div>

        </div>
    );
}
